import api from './api';

const holidayService = {
  // Get all holidays (optionally filter by year)
  getAllHolidays: (year = null) => {
    const params = year ? { year } : {};
    return api.get('/holidays', { params });
  },

  // Get holidays in a date range
  getHolidaysInRange: (startDate, endDate) => {
    return api.get('/holidays/range', {
      params: { startDate, endDate }
    });
  },

  // Add a single holiday
  addHoliday: (holidayData) => {
    return api.post('/holidays', holidayData);
  },

  // Bulk upload holidays
  bulkUploadHolidays: (holidays, year = null, clearExisting = false) => {
    return api.post('/holidays/bulk-upload', {
      holidays,
      year,
      clearExisting
    });
  },

  // Update a holiday
  updateHoliday: (id, holidayData) => {
    return api.put(`/holidays/${id}`, holidayData);
  },

  // Delete a holiday
  deleteHoliday: (id) => {
    return api.delete(`/holidays/${id}`);
  },

  // Delete all holidays for a year
  deleteYearHolidays: (year) => {
    return api.delete(`/holidays/year/${year}`);
  },

  // Parse CSV text for holidays
  // Expected format: date,name,description (optional)
  // Example: 2025-01-01,New Year's Day,National Holiday
  parseHolidayCsv: (csvText) => {
    const lines = csvText.trim().split('\n');
    const holidays = [];

    lines.forEach((line, index) => {
      // Skip header row if present
      if (index === 0 && line.toLowerCase().includes('date')) {
        return;
      }

      const parts = line.split(',').map(p => p.trim());
      if (parts.length >= 2 && parts[0] && parts[1]) {
        holidays.push({
          date: parts[0],
          name: parts[1],
          description: parts[2] || null
        });
      }
    });

    return holidays;
  },

  // Parse Excel file for holidays
  parseHolidayExcel: async (file) => {
    const XLSX = await import('xlsx');

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const holidays = jsonData.map(row => {
            // Handle different possible column names (including common variations)
            // Try to find date, name, description from any reasonable column header
            const rowKeys = Object.keys(row);
            const findCol = (patterns) => {
              for (const pattern of patterns) {
                // Exact match first
                if (row[pattern] !== undefined) return row[pattern];
              }
              // Fuzzy match: find a key that contains any pattern word
              for (const pattern of patterns) {
                const key = rowKeys.find(k => k.toLowerCase().includes(pattern.toLowerCase()));
                if (key) return row[key];
              }
              return undefined;
            };

            const date = findCol(['date', 'Date', 'DATE', 'Date & Day', 'Date and Day']);
            const name = findCol(['name', 'Name', 'NAME', 'holiday', 'Holiday', 'occasion', 'Occasion', 'OCCASION', 'event', 'Event']);
            const description = findCol(['description', 'Description', 'DESCRIPTION', 'type', 'Type', 'category', 'Category']) || null;

            // Convert Excel date serial number if necessary
            let dateStr = date;
            if (typeof date === 'number') {
              const excelDate = XLSX.SSF.parse_date_code(date);
              dateStr = `${excelDate.y}-${String(excelDate.m).padStart(2, '0')}-${String(excelDate.d).padStart(2, '0')}`;
            }

            return {
              date: dateStr,
              name: name,
              description: description
            };
          }).filter(h => h.date && h.name);

          resolve(holidays);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  },

  // Get holiday dates as a Set for quick lookup
  getHolidayDatesSet: async (startDate, endDate) => {
    try {
      const response = await holidayService.getHolidaysInRange(startDate, endDate);
      return new Set(response.data.dates || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      return new Set();
    }
  }
};

export default holidayService;
